import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { fetchOrgIndustry } from "util/copilotChatApi";
import { apiRequest } from "util/services";
import {
  base64FromInt16,
  base64ToBytes,
  buildSonicLiveWsUrl,
  downsampleTo16k,
  industryToSectorSlug,
  isMpegAudioMime,
  parsePcmSampleRate,
  playMpegBase64,
} from "util/sonicVoiceApi";

export const ADVISOR_VOICE_CONNECTION = {
  IDLE: "idle",
  CONNECTING: "connecting",
  READY: "ready",
  ERROR: "error",
};

const SPEECH_LEVEL_THRESHOLD = 0.013;
const TURN_SILENCE_MS = 1400;
const MIN_SPEECH_MS = 500;
const MAX_WS_RECONNECT = 4;

/**
 * Gemini Live WebSocket session for Pivony Advisor Voice (merged into Advisor page).
 *
 * Uplink strategy: stream mic audio continuously (except during advisor playback).
 * Mark turn boundaries with audio_end via client VAD — do not stop the stream
 * between turns; Gemini Live needs ongoing audio to detect the next utterance.
 */
export function useAdvisorVoice({
  languageCode = "en-US",
  enabled = true,
  onTranscript,
} = {}) {
  const { t } = useTranslation();
  const [connection, setConnection] = useState(ADVISOR_VOICE_CONNECTION.IDLE);
  const [liveSessionId, setLiveSessionId] = useState(null);
  const [sector, setSector] = useState("hospitality");
  const [micActive, setMicActive] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [livePreview, setLivePreview] = useState({ user: "", assistant: "" });

  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const processorRef = useRef(null);
  const micStreamRef = useRef(null);
  const playbackCtxRef = useRef(null);
  const playbackTimeRef = useRef(0);
  const activeMp3Ref = useRef(null);
  const voiceSessionRef = useRef({ voice_engine: "auto", elevenlabs_voice_id: "" });
  const micActiveRef = useRef(false);
  const autoStartMicRef = useRef(false);
  const speakingTimeoutRef = useRef(null);
  const levelThrottleRef = useRef(0);
  const isSpeakingRef = useRef(false);
  const vadSpeechRef = useRef(false);
  const vadSilenceSinceRef = useRef(null);
  const vadSpeechSinceRef = useRef(null);
  const audioEndSentRef = useRef(false);
  const keepaliveThrottleRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const reconnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const wsReadyRef = useRef(false);
  const openWsRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const resetVadState = useCallback(() => {
    vadSpeechRef.current = false;
    vadSilenceSinceRef.current = null;
    vadSpeechSinceRef.current = null;
  }, []);

  const allowNextAudioEnd = useCallback(() => {
    audioEndSentRef.current = false;
    resetVadState();
  }, [resetVadState]);

  const signalAudioTurnEnd = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || audioEndSentRef.current) return;
    audioEndSentRef.current = true;
    resetVadState();
    ws.send(JSON.stringify({ type: "audio_end" }));
  }, [resetVadState]);

  const scheduleReconnect = useCallback(() => {
    if (!micActiveRef.current) return;
    if (reconnectAttemptsRef.current >= MAX_WS_RECONNECT) {
      setConnection(ADVISOR_VOICE_CONNECTION.ERROR);
      setErrorText(t("pivony_sonic.console_error_generic"));
      return;
    }
    if (reconnectTimerRef.current) return;
    reconnectAttemptsRef.current += 1;
    wsReadyRef.current = false;
    setConnection(ADVISOR_VOICE_CONNECTION.CONNECTING);
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      if (openWsRef.current) {
        openWsRef.current(true);
      }
    }, 900);
  }, [t]);

  const signalNewUserTurn = useCallback(() => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "audio_start" }));
  }, []);

  const sendSilenceKeepalive = useCallback((inputRate) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const frame = new Int16Array(Math.max(160, Math.floor((inputRate / 16000) * 320)));
    ws.send(
      JSON.stringify({
        type: "audio",
        data: base64FromInt16(frame),
        mime_type: "audio/pcm;rate=16000",
      })
    );
  }, []);

  const finishAdvisorPlayback = useCallback(() => {
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
      speakingTimeoutRef.current = null;
    }
    if (activeMp3Ref.current) {
      activeMp3Ref.current.pause();
      activeMp3Ref.current = null;
    }
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    allowNextAudioEnd();
  }, [allowNextAudioEnd]);

  const scheduleAdvisorPlaybackEnd = useCallback(() => {
    const ctx = playbackCtxRef.current;
    if (!ctx) return;
    const delayMs = Math.max(
      350,
      Math.ceil((playbackTimeRef.current - ctx.currentTime) * 1000) + 350
    );
    if (speakingTimeoutRef.current) {
      clearTimeout(speakingTimeoutRef.current);
    }
    speakingTimeoutRef.current = setTimeout(() => {
      finishAdvisorPlayback();
    }, delayMs);
  }, [finishAdvisorPlayback]);

  const markAdvisorSpeaking = useCallback(() => {
    if (!isSpeakingRef.current) {
      signalAudioTurnEnd();
    }
    isSpeakingRef.current = true;
    setIsSpeaking(true);
    scheduleAdvisorPlaybackEnd();
  }, [scheduleAdvisorPlaybackEnd, signalAudioTurnEnd]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    fetchOrgIndustry().then((row) => {
      if (!cancelled && row?.name) {
        setSector(industryToSectorSlug(row.name));
      }
    });
    apiRequest("sonic/settings", "GET", null, true).then((res) => {
      if (cancelled || !res || res.error) return;
      const vp = res.settings?.voice_provider_config || {};
      voiceSessionRef.current = {
        voice_engine: vp.voice_engine || "auto",
        elevenlabs_voice_id: vp.elevenlabs_voice_id || "",
      };
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const emitTranscript = useCallback((role, text, meta) => {
    const line = String(text || "").trim();
    if (!line) return;
    const isFinal = meta?.final !== false;
    setLivePreview((prev) => {
      if (role === "user") {
        return { user: line, assistant: "" };
      }
      if (role === "assistant" || role === "advisor") {
        return { ...prev, assistant: line };
      }
      return prev;
    });
    if (onTranscriptRef.current && (isFinal || role === "assistant" || role === "advisor")) {
      onTranscriptRef.current({ role, text: line, meta: { ...meta, final: isFinal } });
    }
  }, []);

  const playWsAudio = useCallback(
    async (b64, mimeType) => {
      if (isMpegAudioMime(mimeType)) {
        if (!isSpeakingRef.current) signalAudioTurnEnd();
        isSpeakingRef.current = true;
        setIsSpeaking(true);
        await playMpegBase64(b64, {
          onStart: (audio) => {
            activeMp3Ref.current = audio;
          },
          onEnd: () => {
            activeMp3Ref.current = null;
            finishAdvisorPlayback();
          },
          onError: () => {
            activeMp3Ref.current = null;
            finishAdvisorPlayback();
          },
        });
        return;
      }
      const sampleRate = parsePcmSampleRate(mimeType);
      const bytes = base64ToBytes(b64);
      const int16 = new Int16Array(
        bytes.buffer,
        bytes.byteOffset,
        bytes.byteLength / 2
      );
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i += 1) {
        float32[i] = int16[i] / 32768;
      }

      if (!playbackCtxRef.current) {
        playbackCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = playbackCtxRef.current;
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      const buffer = ctx.createBuffer(1, float32.length, sampleRate);
      buffer.copyToChannel(float32, 0);
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(ctx.destination);
      const startAt = Math.max(ctx.currentTime, playbackTimeRef.current);
      src.start(startAt);
      playbackTimeRef.current = startAt + buffer.duration;
      markAdvisorSpeaking();
    },
    [finishAdvisorPlayback, markAdvisorSpeaking, signalAudioTurnEnd]
  );

  const stopMic = useCallback(() => {
    micActiveRef.current = false;
    setMicActive(false);
    setAudioLevel(0);
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    signalAudioTurnEnd();
  }, [signalAudioTurnEnd]);

  const disconnect = useCallback(() => {
    autoStartMicRef.current = false;
    reconnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    wsReadyRef.current = false;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    stopMic();
    finishAdvisorPlayback();
    setLivePreview({ user: "", assistant: "" });
    const ws = wsRef.current;
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "close" }));
        ws.close();
      }
      wsRef.current = null;
    }
    setConnection(ADVISOR_VOICE_CONNECTION.IDLE);
    setLiveSessionId(null);
    playbackTimeRef.current = 0;
    audioEndSentRef.current = false;
    resetVadState();
  }, [finishAdvisorPlayback, resetVadState, stopMic]);

  const handleWsMessage = useCallback(
    async (raw) => {
      let msg;
      try {
        msg = JSON.parse(raw);
      } catch (_) {
        return;
      }

      if (msg.type === "ready") {
        wsReadyRef.current = true;
        reconnectAttemptsRef.current = 0;
        setConnection(ADVISOR_VOICE_CONNECTION.READY);
        if (msg.session_id) setLiveSessionId(msg.session_id);
        setErrorText(null);
        allowNextAudioEnd();
        return;
      }
      if (msg.type === "transcript") {
        const role = msg.role || "assistant";
        const text = String(msg.text || "").trim();
        if (
          text.includes("Voice mode needs Gemini Live") ||
          text.includes("SONIC_LIVE_GOOGLE_API_KEY")
        ) {
          setConnection(ADVISOR_VOICE_CONNECTION.ERROR);
          setErrorText(t("cx_gpt.voice_live_unavailable"));
          return;
        }
        emitTranscript(role, text, { final: msg.final !== false });
        return;
      }
      if (msg.type === "advisor_query") {
        emitTranscript("advisor", msg.question, t("pivony_sonic.console_advisor_query"));
        return;
      }
      if (msg.type === "audio" && msg.data) {
        try {
          await playWsAudio(msg.data, msg.mime_type);
        } catch (err) {
          setErrorText(String(err));
        }
        return;
      }
      if (msg.type === "error") {
        const errMsg = msg.message || t("pivony_sonic.console_error_generic");
        wsReadyRef.current = false;
        if (micActiveRef.current && msg.recoverable !== false) {
          setErrorText(errMsg);
          scheduleReconnect();
        } else {
          setErrorText(errMsg);
          setConnection(ADVISOR_VOICE_CONNECTION.ERROR);
        }
      }
    },
    [allowNextAudioEnd, emitTranscript, playWsAudio, scheduleReconnect, t]
  );

  const openWs = useCallback(
    async (isReconnect = false) => {
      if (!enabled) return;
      if (wsRef.current && !isReconnect) return;

      if (isReconnect && wsRef.current) {
        const oldWs = wsRef.current;
        oldWs.onclose = null;
        oldWs.onerror = null;
        oldWs.close();
        wsRef.current = null;
      }

      if (!isReconnect) {
        setErrorText(null);
        playbackTimeRef.current = 0;
        allowNextAudioEnd();
      }

      reconnectingRef.current = isReconnect;
      wsReadyRef.current = false;
      setConnection(ADVISOR_VOICE_CONNECTION.CONNECTING);

      const url = await buildSonicLiveWsUrl({
        sessionId: liveSessionId,
        sector,
        languageCode,
        voiceEngine: voiceSessionRef.current.voice_engine || "auto",
        elevenlabsVoiceId: voiceSessionRef.current.elevenlabs_voice_id,
      });
      if (wsRef.current && !isReconnect) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnection(ADVISOR_VOICE_CONNECTION.CONNECTING);
      };
      ws.onmessage = (ev) => {
        handleWsMessage(ev.data);
      };
      ws.onerror = () => {
        if (micActiveRef.current) return;
        setConnection(ADVISOR_VOICE_CONNECTION.ERROR);
        setErrorText(t("pivony_sonic.console_error_ws"));
        autoStartMicRef.current = false;
      };
      ws.onclose = () => {
        const wasReconnecting = reconnectingRef.current;
        reconnectingRef.current = false;
        wsRef.current = null;

        if (!micActiveRef.current && !autoStartMicRef.current) {
          setMicActive(false);
          setAudioLevel(0);
          autoStartMicRef.current = false;
          finishAdvisorPlayback();
          audioEndSentRef.current = false;
          resetVadState();
          setConnection(ADVISOR_VOICE_CONNECTION.IDLE);
          return;
        }

        if (micActiveRef.current && !wasReconnecting) {
          scheduleReconnect();
        }
      };
    },
    [
      allowNextAudioEnd,
      enabled,
      finishAdvisorPlayback,
      handleWsMessage,
      languageCode,
      liveSessionId,
      resetVadState,
      scheduleReconnect,
      sector,
      t,
    ]
  );

  openWsRef.current = openWs;

  const connect = useCallback(() => openWs(false), [openWs]);

  const startMic = useCallback(async () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      message.warning(t("pivony_sonic.console_connect_first"));
      return false;
    }

    if (micActiveRef.current && processorRef.current) {
      return true;
    }

    try {
      const stream =
        micStreamRef.current ||
        (await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: false,
        }));
      micStreamRef.current = stream;

      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        await ctx.resume();
      }

      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      const inputRate = ctx.sampleRate;
      allowNextAudioEnd();

      processor.onaudioprocess = (ev) => {
        if (
          !micActiveRef.current ||
          !wsReadyRef.current ||
          !wsRef.current ||
          wsRef.current.readyState !== WebSocket.OPEN
        ) {
          return;
        }

        if (audioCtxRef.current?.state === "suspended") {
          audioCtxRef.current.resume();
        }

        const channel = ev.inputBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < channel.length; i += 1) {
          sum += channel[i] * channel[i];
        }
        const rms = Math.sqrt(sum / channel.length);
        const now = Date.now();

        if (now - levelThrottleRef.current > 80) {
          levelThrottleRef.current = now;
          setAudioLevel(Math.min(1, rms * 14));
        }

        if (isSpeakingRef.current) {
          if (now - keepaliveThrottleRef.current > 250) {
            keepaliveThrottleRef.current = now;
            sendSilenceKeepalive(inputRate);
          }
          return;
        }

        const isSpeech = rms > SPEECH_LEVEL_THRESHOLD;
        if (isSpeech) {
          if (audioEndSentRef.current) {
            signalNewUserTurn();
            allowNextAudioEnd();
          }
          vadSpeechRef.current = true;
          vadSilenceSinceRef.current = null;
          if (!vadSpeechSinceRef.current) {
            vadSpeechSinceRef.current = now;
          }
        } else if (vadSpeechRef.current && !audioEndSentRef.current) {
          const spokeMs = vadSpeechSinceRef.current
            ? now - vadSpeechSinceRef.current
            : 0;
          if (spokeMs < MIN_SPEECH_MS) {
            return;
          }
          if (!vadSilenceSinceRef.current) {
            vadSilenceSinceRef.current = now;
          } else if (now - vadSilenceSinceRef.current >= TURN_SILENCE_MS) {
            signalAudioTurnEnd();
          }
        }

        const pcm = downsampleTo16k(channel, inputRate);
        wsRef.current.send(
          JSON.stringify({
            type: "audio",
            data: base64FromInt16(pcm),
            mime_type: "audio/pcm;rate=16000",
          })
        );
      };

      source.connect(processor);
      processor.connect(ctx.destination);
      micActiveRef.current = true;
      setMicActive(true);
      return true;
    } catch (err) {
      setErrorText(String(err));
      message.error(t("pivony_sonic.console_mic_denied"));
      return false;
    }
  }, [allowNextAudioEnd, sendSilenceKeepalive, signalAudioTurnEnd, signalNewUserTurn, t]);

  const connectAndListen = useCallback(() => {
    if (connection === ADVISOR_VOICE_CONNECTION.READY) {
      if (!micActiveRef.current) {
        startMic();
      }
      return;
    }
    if (connection === ADVISOR_VOICE_CONNECTION.CONNECTING) return;
    autoStartMicRef.current = true;
    connect();
  }, [connect, connection, startMic]);

  useEffect(() => {
    if (!autoStartMicRef.current) return undefined;
    if (connection !== ADVISOR_VOICE_CONNECTION.READY) return undefined;
    autoStartMicRef.current = false;
    startMic();
    return undefined;
  }, [connection, startMic]);

  const sendTextOverVoice = useCallback(
    (text) => {
      const line = String(text || "").trim();
      const ws = wsRef.current;
      if (!line || !ws || ws.readyState !== WebSocket.OPEN) return false;
      allowNextAudioEnd();
      ws.send(JSON.stringify({ type: "text", text: line }));
      emitTranscript("user", line);
      return true;
    },
    [allowNextAudioEnd, emitTranscript]
  );

  const toggleMic = useCallback(async () => {
    if (micActiveRef.current) {
      stopMic();
      return;
    }
    await startMic();
  }, [startMic, stopMic]);

  useEffect(() => {
    if (!enabled) {
      disconnect();
    }
  }, [enabled, disconnect]);

  useEffect(
    () => () => {
      finishAdvisorPlayback();
      disconnect();
    },
    [disconnect, finishAdvisorPlayback]
  );

  return {
    connection,
    liveSessionId,
    sector,
    micActive,
    audioLevel,
    isSpeaking,
    livePreview,
    errorText,
    setErrorText,
    connect,
    connectAndListen,
    disconnect,
    startMic,
    stopMic,
    toggleMic,
    sendTextOverVoice,
    isConnected: connection === ADVISOR_VOICE_CONNECTION.READY,
    isConnecting: connection === ADVISOR_VOICE_CONNECTION.CONNECTING,
    isVoiceActive: connection !== ADVISOR_VOICE_CONNECTION.IDLE,
  };
}
